import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, CheckCircle, MessageCircle, Star, AlertTriangle, LifeBuoy, ShoppingBag, Package, Globe, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { mockBookings } from '../mockData';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import ConversationList from '../components/messaging/ConversationList';
import MessageThread from '../components/messaging/MessageThread';
import ReviewModal from '../components/ReviewModal';
import ReportTrainerModal from '../components/ReportTrainerModal';
import { toast } from '../hooks/use-toast';
import { useLocation, Link } from 'react-router-dom';
import UserSupportTickets from './UserSupportTickets';
import OrderDetailModal from '../components/OrderDetailModal';
import { getBookingTimeDisplay, getTimezoneAbbr } from '../utils/timezoneUtils';
import LegalDisclaimerBox from '../components/LegalDisclaimerBox';
import PaymentInfoTab from '../components/PaymentInfoTab';

const UserDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [userPresences, setUserPresences] = useState({});
  const [unreviewedBookings, setUnreviewedBookings] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [currentBookingToReview, setCurrentBookingToReview] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentTrainerToReport, setCurrentTrainerToReport] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  const location = useLocation();
  const isInitialMount = useRef(true);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Helper function to check for ban in API responses
  const checkBanResponse = async (response) => {
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.detail?.error === 'account_banned') {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('cart_id');
          window.location.href = '/login?banned=true';
          return true;
        }
      } catch (e) {
        // Not a JSON response
      }
    }
    return false;
  };

  // Initial fetch and setup intervals
  useEffect(() => {
    fetchBookings();
    fetchConversations();
    fetchUnreviewedBookings();
    fetchRecentOrders();
    
    // Presence heartbeat - send every 60 seconds
    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE}/api/presence/heartbeat`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'online' })
        });
      } catch (error) {
        console.error('Failed to send presence heartbeat:', error);
      }
    };
    
    sendHeartbeat(); // Send immediately
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);
    const pollingInterval = setInterval(() => {
      fetchConversations();
      fetchBookings();
    }, 10000);
    
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(pollingInterval);
    };
  }, []);

  // Fetch bookings when returning to dashboard (location change or page visibility)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Refresh bookings when user returns to dashboard
    console.log('Location changed or dashboard visible - refreshing bookings');
    fetchBookings();
    fetchConversations();
    fetchUnreviewedBookings();
  }, [location.pathname]);

  // Handle page visibility changes (user switches tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible - refreshing bookings');
        fetchBookings();
        fetchConversations();
        fetchUnreviewedBookings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found');
        setIsLoadingBookings(false);
        return;
      }

      console.log('Fetching bookings from:', `${API_BASE}/api/bookings/user`);
      const response = await fetch(`${API_BASE}/api/bookings/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for ban
      if (await checkBanResponse(response)) return;
      
      console.log('Bookings response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Bookings data received:', data);
        setBookings(data);
      } else {
        console.error('Failed to fetch bookings:', response.status);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const fetchUnreviewedBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/api/user/unreviewed-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreviewedBookings(data);
        
        // Otomatik modal açılmasını KALDIRDIK - artık kullanıcı butona tıklayarak açacak
      }
    } catch (error) {
      console.error('Error fetching unreviewed bookings:', error);
    }
  };


  const fetchRecentOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/orders/user?page=1&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };


  const handleReviewSubmitSuccess = () => {
    // Refresh unreviewed bookings
    fetchUnreviewedBookings();
    fetchBookings();
  };

  const handleMarkAsComplete = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to continue',
          variant: 'destructive'
        });
        return;
      }

      // bookingId is already the MongoDB ObjectId (stored in 'id' field)
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: 'Success!',
          description: 'Session marked as complete. You can now leave a review.'
        });
        // Refresh bookings to show updated status
        fetchBookings();
        fetchUnreviewedBookings();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to mark as complete',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error marking booking as complete:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark booking as complete',
        variant: 'destructive'
      });
    }
  };


  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoadingConversations(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        
        // Calculate unread count
        const unread = (data.conversations || []).reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadCount(unread);
        
        // Fetch presence status for all conversation participants
        const otherUserIds = (data.conversations || []).map(conv => conv.other_user_id).filter(Boolean);
        if (otherUserIds.length > 0) {
          try {
            const presenceResponse = await fetch(`${API_BASE}/api/presence/bulk`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ user_ids: otherUserIds })
            });
            
            if (presenceResponse.ok) {
              const presenceData = await presenceResponse.json();
              setUserPresences(presenceData.presences || {});
            }
          } catch (presenceError) {
            console.error('Error fetching presence data:', presenceError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');
  
  // Recent Bookings: Completed but NO action taken yet (neither reviewed nor reported)
  const recentBookings = bookings.filter(b => 
    b.status === 'completed' && !b.reviewed && !b.reported
  );
  
  // Past Sessions: Completed and action taken (reviewed OR reported)
  const pastBookings = bookings.filter(b => 
    b.status === 'completed' && (b.reviewed || b.reported)
  );

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 lg:p-6">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Welcome back, {user?.name}!</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">Manage your bookings and connect with trainers</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          <div className="overflow-hidden border-2 border-cyan-200 dark:border-cyan-800 hover:shadow-xl transition-all rounded-lg">
            <div className="p-4 sm:p-6 bg-gradient-to-br from-cyan-50 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-cyan-700 dark:text-cyan-300 mb-1 font-semibold">Upcoming Sessions</p>
                  <p className="text-3xl sm:text-4xl font-black text-cyan-600 dark:text-cyan-400">{upcomingBookings.length}</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-2 sm:p-3 rounded-2xl shadow-lg flex-shrink-0">
                  <Calendar className="w-6 h-6 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden border-2 border-green-200 dark:border-green-800 hover:shadow-xl transition-all rounded-lg">
            <div className="p-4 sm:p-6 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-green-700 dark:text-green-300 mb-1 font-semibold">Completed Sessions</p>
                  <p className="text-3xl sm:text-4xl font-black text-green-600 dark:text-green-400">{pastBookings.length}</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-2 sm:p-3 rounded-2xl shadow-lg flex-shrink-0">
                  <CheckCircle className="w-6 h-6 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden border-2 border-red-200 dark:border-red-800 hover:shadow-xl transition-all rounded-lg">
            <div className="p-4 sm:p-6 bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-red-700 dark:text-red-300 mb-1 font-semibold">Unread Messages</p>
                  <p className="text-3xl sm:text-4xl font-black text-red-600 dark:text-red-400">{unreadCount}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-pink-500 p-2 sm:p-3 rounded-2xl shadow-lg flex-shrink-0">
                  <MessageCircle className="w-6 h-6 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Legal Disclaimer Notice - Dynamic from API */}
        <LegalDisclaimerBox variant="compact" className="mb-6" />

        {/* Bookings and Messages Tabs */}
        <Tabs 
          value={activeTab} 
          onValueChange={(value) => {
            setActiveTab(value);
            // Refresh data when switching to bookings tab
            if (value === 'bookings') {
              console.log('Bookings tab selected - refreshing data');
              fetchBookings();
              fetchUnreviewedBookings();
            } else if (value === 'messages') {
              fetchConversations();
            } else if (value === 'support') {
              // Support tickets tab selected
              console.log('Support tickets tab selected');
            }
          }}
          className="w-full"
        >
          <TabsList className="grid w-full max-w-3xl grid-cols-5 h-auto gap-0.5 sm:gap-1 p-1 sm:p-1.5">
            {/* Payment Info Tab - First Position with Red Border */}
            <TabsTrigger 
              value="payment-info" 
              className="relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-sm py-2 sm:py-3 px-1 sm:px-3 font-bold border-2 border-red-500 bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/40 data-[state=active]:bg-red-500 data-[state=active]:text-white data-[state=active]:border-red-600 transition-all rounded-md"
            >
              <DollarSign className="w-4 h-4 sm:w-4 sm:h-4" />
              <span>Pay</span>
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md"
            >
              <Calendar className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">My Bookings</span>
              <span className="sm:hidden">Bookings</span>
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md"
            >
              <ShoppingBag className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">My Orders</span>
              <span className="sm:hidden">Orders</span>
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md"
            >
              <MessageCircle className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Messages</span>
              <span className="sm:hidden">Msgs</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="support" 
              className="support-tickets-tab relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-sm py-2 sm:py-3 px-1 sm:px-3 font-bold transition-all rounded-md"
            >
              <LifeBuoy className="w-4 h-4 sm:w-4 sm:h-4" />
              <span>Help</span>
            </TabsTrigger>
          </TabsList>

          {/* Payment Info Tab Content */}
          <TabsContent value="payment-info" className="space-y-4 mt-4">
            <PaymentInfoTab />
          </TabsContent>

          <TabsContent value="bookings" className="space-y-4 mt-4">
            {isLoadingBookings ? (
              <Card>
                <CardContent className="py-12">
                  <p className="text-center text-gray-500">Loading bookings...</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Upcoming Sessions - Confirmed/Pending Bookings */}
                {upcomingBookings.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-cyan-200 dark:border-cyan-700 shadow-sm">
                    <div className="p-6 border-b border-cyan-200 dark:border-cyan-700 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-cyan-600" />
                        Upcoming Sessions
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Your scheduled sessions with trainers
                      </p>
                    </div>
                    <div className="p-6 space-y-3 sm:space-y-4">
                      {upcomingBookings.map((booking) => {
                        const getStatusBadge = (status) => {
                          const configs = {
                            pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pending' },
                            confirmed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Confirmed' },
                          };
                          const config = configs[status] || configs.pending;
                          return <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>;
                        };

                        return (
                          <div key={booking.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-2 border-cyan-100 dark:border-cyan-800 rounded-lg gap-3 hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors bg-white dark:bg-gray-800/50">
                            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                              <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                                <AvatarFallback className="bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400">
                                  {booking.trainer_name ? booking.trainer_name.charAt(0) : 'T'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-semibold text-sm sm:text-base truncate text-gray-900 dark:text-white">
                                  {booking.trainer_name || 'Trainer'}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {booking.specialization || 'Session'}
                                </p>
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-xs sm:text-sm">
                                  <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                    {new Date(booking.date).toLocaleDateString()}
                                  </span>
                                  {/* Time with timezone conversion */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 dark:bg-green-900/40 rounded-md border border-green-200 dark:border-green-700">
                                      <Clock className="w-3 h-3 text-green-600 dark:text-green-400" />
                                      <span className="text-xs font-medium text-green-700 dark:text-green-300">
                                        Your time: {(() => {
                                          const timeDisplay = getBookingTimeDisplay(booking, booking.trainer_timezone, user?.timezone);
                                          return timeDisplay.showBoth ? timeDisplay.convertedTime : booking.time;
                                        })()} {getTimezoneAbbr(user?.timezone)}
                                      </span>
                                    </div>
                                    {booking.trainer_timezone && booking.trainer_timezone !== user?.timezone && (
                                      <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 rounded-md border border-blue-200 dark:border-blue-700">
                                        <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                          Trainer's time: {booking.time} {getTimezoneAbbr(booking.trainer_timezone)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                {getStatusBadge(booking.status)}
                                <p className="text-base sm:text-lg font-bold text-red-600 dark:text-red-400">${booking.price}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Bookings - Completed */}
                {recentBookings.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Recent Bookings</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Completed sessions waiting for review or report
                      </p>
                    </div>
                    <div className="p-6 space-y-3 sm:space-y-4">
                      {recentBookings.map((booking) => {
                      const getStatusBadge = (status) => {
                        const configs = {
                          pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pending' },
                          confirmed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Confirmed' },
                          completed: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', label: 'Completed' },
                          cancelled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Cancelled' }
                        };
                        const config = configs[status] || configs.pending;
                        return <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>;
                      };

                      return (
                        <div key={booking.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg gap-3 hover:border-purple-300 dark:hover:border-purple-600 transition-colors bg-white dark:bg-gray-800/50">
                          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                            <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                              <AvatarFallback className="bg-purple-100 dark:bg-purple-900/50 text-red-600 dark:text-red-400">
                                {booking.trainer_name ? booking.trainer_name.charAt(0) : 'T'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-sm sm:text-base truncate text-gray-900 dark:text-white">
                                {booking.trainer_name || 'Trainer'}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                {booking.specialization || 'Session'}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {new Date(booking.date).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {(() => {
                                    const timeDisplay = getBookingTimeDisplay(booking, booking.trainer_timezone, user?.timezone);
                                    if (timeDisplay.showBoth) {
                                      return (
                                        <span title={`Trainer time: ${timeDisplay.originalTime} ${getTimezoneAbbr(booking.trainer_timezone)}`}>
                                          {timeDisplay.convertedTime}
                                          <span className="text-xs ml-1 text-gray-400">({getTimezoneAbbr(user?.timezone)})</span>
                                        </span>
                                      );
                                    }
                                    return booking.time;
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                              {getStatusBadge(booking.status)}
                              <p className="text-base sm:text-lg font-bold text-red-600 dark:text-red-400">${booking.price}</p>
                            </div>
                            
                            {/* Action buttons based on status */}
                            {booking.status === 'confirmed' && (
                              <Button
                                size="sm"
                                onClick={() => handleMarkAsComplete(booking.id)}
                                className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto text-xs"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Mark as Complete
                              </Button>
                            )}
                            
                            {booking.status === 'completed' && (
                              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                                {/* Review Button - sadece reviewed değilse */}
                                {!booking.reviewed && (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setCurrentBookingToReview(booking);
                                      setReviewModalOpen(true);
                                    }}
                                    className="bg-purple-600 hover:bg-purple-700 text-white flex-1 sm:flex-none text-xs"
                                  >
                                    <Star className="w-3 h-3 mr-1" />
                                    Leave Review
                                  </Button>
                                )}
                                
                                {/* Reviewed Badge - review yapıldıysa */}
                                {booking.reviewed && (
                                  <span className="text-xs text-green-600 font-medium flex items-center gap-1 px-2 py-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Reviewed
                                  </span>
                                )}
                                
                                {/* Reported Badge - report yapıldıysa */}
                                {booking.reported && (
                                  <span className="text-xs text-orange-600 font-medium flex items-center gap-1 px-2 py-1 bg-orange-50 dark:bg-orange-900/20 rounded">
                                    <AlertTriangle className="w-3 h-3" />
                                    Reported
                                  </span>
                                )}
                                
                                {/* Report Trainer Button - sadece reported değilse göster */}
                                {!booking.reported && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setCurrentTrainerToReport({
                                        id: booking.trainer_id,
                                        name: booking.trainer_name,
                                        booking_id: booking.id  // Booking ID'yi de gönder
                                      });
                                      setReportModalOpen(true);
                                    }}
                                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 flex-1 sm:flex-none text-xs"
                                  >
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Report Trainer
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}

                {bookings.length === 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="py-12">
                      <div className="text-center text-gray-500 dark:text-gray-400">
                        <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">No bookings yet</p>
                        <p className="text-xs sm:text-sm mt-2 text-gray-500 dark:text-gray-400">Book your first session with a trainer!</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {pastBookings.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Past Sessions</h3>
                </div>
              <div className="p-6 space-y-3 sm:space-y-4">
                {pastBookings.map((booking) => (
                  <div key={booking.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg gap-3 bg-white dark:bg-gray-800/50">
                    <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                      <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                        <AvatarFallback className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{booking.trainer_name ? booking.trainer_name.charAt(0) : 'T'}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-sm sm:text-base truncate text-gray-900 dark:text-white">{booking.trainer_name || 'Trainer'}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{booking.specialization}</p>
                        <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          <span>{booking.date}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Review and Report status/buttons */}
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      {/* Reviewed Badge */}
                      {booking.reviewed && (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1 px-2 py-1">
                          <CheckCircle className="w-4 h-4" />
                          Reviewed
                        </span>
                      )}
                      
                      {/* Reported Badge */}
                      {booking.reported && (
                        <span className="text-xs text-orange-600 font-medium flex items-center gap-1 px-2 py-1 bg-orange-50 dark:bg-orange-900/20 rounded">
                          <AlertTriangle className="w-4 h-4" />
                          Reported
                        </span>
                      )}
                      
                      {/* Leave Review button - sadece reviewed değilse */}
                      {!booking.reviewed && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setCurrentBookingToReview(booking);
                            setReviewModalOpen(true);
                          }}
                          className="flex-1 sm:flex-none text-xs sm:text-sm touch-manipulation bg-purple-50 dark:bg-purple-900/30 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 border-purple-300 dark:border-purple-700"
                        >
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Leave Review
                        </Button>
                      )}
                      
                      {/* Report Trainer button - sadece reported değilse */}
                      {!booking.reported && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentTrainerToReport({
                              id: booking.trainer_id,
                              name: booking.trainer_name,
                              booking_id: booking.id
                            });
                            setReportModalOpen(true);
                          }}
                          className="flex-1 sm:flex-none text-xs sm:text-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Report Trainer
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

              </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="lg:col-span-1 order-2 lg:order-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Conversations</h3>
                  </div>
                  <div className="p-0">
                    <div className="max-h-[400px] lg:max-h-[600px] overflow-y-auto">
                      <ConversationList
                        conversations={conversations}
                        selectedId={selectedConversationId}
                        onSelect={setSelectedConversationId}
                        currentUserId={user?.id}
                        userPresences={userPresences}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 order-1 lg:order-2">
                <Card className="h-[500px] sm:h-[600px]">
                  {selectedConversationId ? (
                    <MessageThread
                      conversationId={selectedConversationId}
                      currentUserId={user?.id}
                      onBack={() => setSelectedConversationId(null)}
                    />
                  ) : (
                    <CardContent className="flex items-center justify-center h-full p-4">
                      <div className="text-center text-gray-500">
                        <MessageCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-base sm:text-lg font-medium">Select a conversation</p>
                        <p className="text-xs sm:text-sm mt-1">Choose a conversation from the list to start messaging</p>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  My Orders
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">Track your recent orders</p>
              </CardHeader>
              <CardContent>
                {recentOrders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-base">No orders yet</p>
                    <p className="text-sm mt-2">Your orders will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="border rounded-lg p-4 hover:border-purple-300 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                              Order #{order.order_number}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(order.created_at).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <Badge 
                            className={
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'shipped' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {order.status === 'pending' ? 'Beklemede' :
                             order.status === 'processing' ? 'Hazırlanıyor' :
                             order.status === 'shipped' ? 'Gönderildi' :
                             order.status}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          {order.items && order.items.map((item, idx) => (
                            <div key={item.product_id || `${item.name}-${idx}`} className="flex justify-between text-sm">
                              <span className="text-gray-700 dark:text-gray-300">
                                {item.name} x {item.quantity}
                              </span>
                              <span className="font-medium">${item.price}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-3 border-t flex justify-between items-center">
                          <span className="text-sm font-semibold">Total:</span>
                          <span className="text-lg font-bold text-red-600">${order.total}</span>
                        </div>
                        
                        {order.status === 'shipped' && order.shipped_at && (
                          <div className="mt-3 text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Shipped on {new Date(order.shipped_at).toLocaleDateString('tr-TR')}
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-t">
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderModal(true);
                            }}
                            className="text-sm text-red-600 hover:text-red-700 font-medium hover:underline"
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <div className="text-center mt-4">
                      <Link to="/orders">
                        <Button variant="outline" className="w-full sm:w-auto">
                          View All Orders
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support" className="space-y-4 mt-4">
            <UserSupportTickets />
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Modal - Manuel Açılır (Buton ile) */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        booking={currentBookingToReview}
        onSubmitSuccess={handleReviewSubmitSuccess}
      />

      {/* Report Trainer Modal */}
      <ReportTrainerModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        trainer={currentTrainerToReport}
        onReportSuccess={(bookingId) => {
          toast({
            title: '✅ Report Submitted',
            description: 'Your report has been submitted to admin for review.'
          });
          setReportModalOpen(false);
          // Refresh bookings to move reported booking to Past Sessions
          fetchBookings();
        }}
      />

      {/* Order Detail Modal */}
      <OrderDetailModal 
        order={selectedOrder}
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
      />
    </DashboardLayout>
  );
};

export default UserDashboard;

// Custom CSS for Support Tickets tab styling
const style = document.createElement('style');
style.innerHTML = `
  .support-tickets-tab {
    background: linear-gradient(to right, #e9d5ff, #fce7f3) !important;
    color: #7e22ce !important;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
    border: 2px solid transparent !important;
  }
  
  .support-tickets-tab:hover {
    background: linear-gradient(to right, #d8b4fe, #fbcfe8) !important;
    transform: scale(1.02);
  }
  
  .support-tickets-tab[data-state="active"] {
    background: linear-gradient(to right, #9333ea, #ec4899) !important;
    color: #ffffff !important;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
    border: 2px solid #ec4899 !important;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}